import { createAccount } from '@turnkey/viem';
import { HttpRestClient } from '@injectivelabs/utils';
import { getInjectiveAddress } from '@injectivelabs/sdk-ts';
import { Turnkey, SessionType } from '@turnkey/sdk-browser';
import { WalletAction, TurnkeyProvider } from '@injectivelabs/wallet-base';
import { ErrorType, WalletException, GeneralException, UnspecifiedErrorCode, TurnkeyWalletSessionException, } from '@injectivelabs/exceptions';
import { TurnkeyOtpWallet } from './otp.js';
import { TurnkeyErrorCodes } from '../types.js';
import { TurnkeyOauthWallet } from './oauth.js';
import { generateGoogleUrl } from '../../utils.js';
import { TURNKEY_OAUTH_PATH, TURNKEY_OTP_INIT_PATH, TURNKEY_OTP_VERIFY_PATH, } from '../consts.js';
export class TurnkeyWallet {
    otpId;
    turnkey;
    userOrganizationId;
    client;
    metadata;
    indexedDbClient;
    accountMap = {};
    setMetadata(metadata) {
        this.metadata = { ...this.metadata, ...metadata };
    }
    constructor(metadata) {
        this.metadata = metadata;
        this.client = new HttpRestClient(metadata.apiServerEndpoint);
    }
    static async getTurnkeyInstance(metadata) {
        const { turnkey, indexedDbClient } = await createTurnkeyClient(metadata);
        return {
            turnkey,
            indexedDbClient,
        };
    }
    async getTurnkey() {
        if (!this.indexedDbClient) {
            await this.initClient();
        }
        if (!this.turnkey) {
            this.turnkey = new Turnkey(this.metadata);
        }
        return this.turnkey;
    }
    async getIndexedDbClient() {
        if (!this.indexedDbClient) {
            await this.initClient();
        }
        if (!this.indexedDbClient) {
            throw new WalletException(new Error('Indexed DB client not initialized'));
        }
        return this.indexedDbClient;
    }
    async getSession(existingCredentialBundle) {
        try {
            const { metadata } = this;
            const indexedDbClient = await this.getIndexedDbClient();
            const turnkey = await this.getTurnkey();
            const session = await turnkey.getSession();
            const organizationId = session?.organizationId || metadata.defaultOrganizationId;
            const credentialBundle = existingCredentialBundle || session?.token;
            if (!credentialBundle) {
                return {
                    session: undefined,
                    organizationId,
                };
            }
            const user = await indexedDbClient.getWhoami();
            const actualOrganizationId = user?.organizationId || session?.organizationId || organizationId;
            if (!user) {
                return {
                    session: undefined,
                    organizationId: actualOrganizationId,
                };
            }
            this.userOrganizationId = actualOrganizationId;
            return {
                session,
                organizationId: actualOrganizationId,
            };
        }
        catch {
            throw new TurnkeyWalletSessionException(new Error('Session expired. Please login again.'));
        }
    }
    async getAccounts() {
        const indexedDbClient = await this.getIndexedDbClient();
        if (!this.userOrganizationId) {
            return [];
        }
        try {
            const response = await indexedDbClient.getWallets({
                organizationId: this.userOrganizationId,
            });
            const accounts = await Promise.allSettled(response.wallets.map((wallet) => indexedDbClient.getWalletAccounts({
                walletId: wallet.walletId,
                organizationId: this.userOrganizationId,
            })));
            const filteredAccounts = accounts
                .filter((account) => account.status === 'fulfilled')
                .flatMap((result) => result.value?.accounts)
                .filter((wa) => !!wa &&
                wa.addressFormat === 'ADDRESS_FORMAT_ETHEREUM' &&
                !!wa.address);
            return filteredAccounts.map((account) => getInjectiveAddress(account.address));
        }
        catch (e) {
            if (e.code === TurnkeyErrorCodes.UserLoggedOut) {
                throw new WalletException(new Error('User is not logged in'), {
                    code: UnspecifiedErrorCode,
                    type: ErrorType.WalletError,
                    contextModule: WalletAction.GetAccounts,
                    contextCode: TurnkeyErrorCodes.UserLoggedOut,
                });
            }
            throw new WalletException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: 'turnkey-wallet-get-accounts',
            });
        }
    }
    async getOrCreateAndGetAccount(address) {
        const { accountMap } = this;
        const indexedDbClient = await this.getIndexedDbClient();
        const organizationId = this.userOrganizationId;
        if (accountMap[address] || accountMap[address.toLowerCase()]) {
            return accountMap[address] || accountMap[address.toLowerCase()];
        }
        if (!organizationId) {
            throw new WalletException(new Error('Organization ID is required'));
        }
        indexedDbClient.config.organizationId = organizationId;
        if (!address) {
            throw new WalletException(new Error('Account address not found'));
        }
        const turnkeyAccount = await createAccount({
            organizationId,
            signWith: address,
            client: indexedDbClient,
        });
        this.accountMap[address] = turnkeyAccount;
        return turnkeyAccount;
    }
    async initOTP(email) {
        const indexedDbClient = await this.getIndexedDbClient();
        const result = await TurnkeyOtpWallet.initEmailOTP({
            client: this.client,
            indexedDbClient,
            email,
            otpInitPath: this.metadata.otpInitPath || TURNKEY_OTP_INIT_PATH,
        });
        if (!result || !result.otpId) {
            throw new WalletException(new Error('Failed to initialize OTP'));
        }
        if (result?.organizationId) {
            this.userOrganizationId = result.organizationId;
        }
        if (result?.otpId) {
            this.otpId = result.otpId;
        }
        return result;
    }
    async confirmOTP(otpCode) {
        const indexedDbClient = await this.getIndexedDbClient();
        const targetPublicKey = await indexedDbClient.getPublicKey();
        if (!this.otpId) {
            throw new WalletException(new Error('OTP ID is required'));
        }
        if (!targetPublicKey) {
            throw new WalletException(new Error('Target public key not found'));
        }
        if (!this.userOrganizationId) {
            throw new WalletException(new Error('Organization ID is required'));
        }
        const result = await TurnkeyOtpWallet.confirmEmailOTP({
            otpCode,
            client: this.client,
            emailOTPId: this.otpId,
            organizationId: this.userOrganizationId,
            targetPublicKey,
            otpVerifyPath: this.metadata.otpVerifyPath || TURNKEY_OTP_VERIFY_PATH,
        });
        if (!result || !result.session) {
            throw new WalletException(new Error('Failed to confirm OTP'));
        }
        await indexedDbClient.loginWithSession(result.session);
        this.userOrganizationId = result.organizationId;
        return result;
    }
    async initOAuth(provider) {
        if (provider === TurnkeyProvider.Apple) {
            throw new WalletException(new Error('Apple sign in option is currently not supported'));
        }
        const indexedDbClient = await this.getIndexedDbClient();
        const nonce = await TurnkeyOauthWallet.generateOAuthNonce(indexedDbClient);
        if (!this.metadata?.googleClientId || !this.metadata?.googleRedirectUri) {
            throw new WalletException(new Error('googleClientId and googleRedirectUri are required'));
        }
        return generateGoogleUrl({
            nonce,
            clientId: this.metadata.googleClientId,
            redirectUri: this.metadata.googleRedirectUri,
        });
    }
    async confirmOAuth(provider, oidcToken) {
        if (provider === TurnkeyProvider.Apple) {
            throw new WalletException(new Error('Apple sign in option is currently not supported'));
        }
        const indexedDbClient = await this.getIndexedDbClient();
        const oauthResult = await TurnkeyOauthWallet.oauthLogin({
            oidcToken,
            indexedDbClient,
            client: this.client,
            providerName: provider.toString(),
            oauthLoginPath: this.metadata.oauthLoginPath || TURNKEY_OAUTH_PATH,
        });
        if (!oauthResult || !oauthResult.credentialBundle) {
            throw new WalletException(new Error('Unexpected OAuth result'));
        }
        await indexedDbClient.loginWithSession(oauthResult.credentialBundle);
        this.userOrganizationId = oauthResult.organizationId;
        return oauthResult.credentialBundle;
    }
    async refreshSession() {
        const session = await this.getSession();
        const indexedDbClient = await this.getIndexedDbClient();
        if (session.session?.token) {
            await indexedDbClient.refreshSession({
                sessionType: SessionType.READ_WRITE,
                expirationSeconds: this.metadata.expirationSeconds,
            });
            this.userOrganizationId = session.organizationId;
            return session.session.token;
        }
        throw new TurnkeyWalletSessionException(new Error('Session expired. Please login again.'));
    }
    async initClient() {
        const { metadata } = this;
        const { turnkey, indexedDbClient } = await createTurnkeyClient(metadata);
        this.turnkey = turnkey;
        this.indexedDbClient = indexedDbClient;
        return { turnkey, indexedDbClient };
    }
}
async function createTurnkeyClient(metadata) {
    const turnkey = new Turnkey(metadata);
    const indexedDbClient = await turnkey.indexedDbClient();
    await indexedDbClient.init();
    if (!turnkey) {
        throw new GeneralException(new Error('Turnkey is not initialized'));
    }
    return {
        turnkey,
        indexedDbClient,
    };
}
